import React from 'react';

import { FCWithChildren } from '@wix/challenges-web-library';
import {
  isStepAvailableForComplete,
  isStepOverdue,
  isStepResolved,
} from '../../../../utils';
import { useResolveStep } from '../../../../../../../../contexts/ResolveStep/ResolveStepContext';
import { useSettings } from '@wix/tpa-settings/react';
import { useSections } from '../../../../../../../../contexts/ParticipantSections/ParticipantSectionsContext';
import { useLoading } from '../../../../../../../../contexts/Loading/LoadingContext';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { ButtonSize as ButtonSIZE } from '@wix/wix-ui-tpa/cssVars';
import { useStepIsUnavailable } from '../utils/useStepIsUnavailable';
import { useSidebarLayoutBase } from '../../../contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { useVideoStepData } from '../../../../../../../../hooks/useVideoStepData';
import { isUserCompleted } from '../../../../../../../../contexts/User/helpers/userTypeHandlers';
import { useUser } from '../../../../../../../../contexts/User/UserContext';
import TextWithDirection from '../../../../../../../../components-shared/TextWithDirection';
import { PreviewActionBlockerContainer } from '../../../../../../../../components-shared/ActionBlocker/PreviewActionBlockerContainer';
import { ActionBlockerAppearance } from '../../../../../../../../components-shared/ActionBlocker/ActionBlocker';
import { SidebarLayoutButton } from '../../../../../components/Buttons/SidebarLayoutButton/SidebarLayoutButton';
import { StepsNavigation } from './components/StepsNavigation';
import { StepUndo } from './components/StepUndo';
import challengeSettings from '../../../../../../settingsParams';
import { IControl } from '../utils/types';
import {
  FocusPoint,
  useFocusManager,
} from '../../../../../../../../contexts/FocusManager';
import { ParticipantStep } from '../../../../../../../../types/v3Types';
import { useCSSPBEnabled } from '../../../../../../../../hooks/useCSSPBStyle';
import { classes, st } from './StepControls.st.css';

export interface IStepControls extends IControl {
  completeBtnDataHook?: string;
  nextBtnDataHook?: string;
  currentStep: ParticipantStep;
  buttonState: string;
  onStepResolve(currentStep: ParticipantStep): Promise<void>;
}

export const StepControlsV1: FCWithChildren<IStepControls> = (props) => {
  const { t } = useTranslation();
  const { isMobile, isRTL } = useEnvironment();
  const settings = useSettings();
  const { participant } = useUser();
  const { isResolveStepRequestInProgress } = useResolveStep();
  const stepId = props.currentStep?.id;
  const isCompletedChallenge = isUserCompleted(
    participant?.transitions?.[0]?.state,
  );
  const cssPBEnabled = useCSSPBEnabled({
    includeParticipantPageAlignmentCheck: true,
  });

  const { isStepUnavailable, isSectionLocked } = useStepIsUnavailable();
  const { stepDescriptionLoaded } = useSections();
  const { hasRicosLoaded } = useLoading();
  const { setNextFocusPoint } = useFocusManager();

  const isCompleteButtonShown =
    !isStepUnavailable &&
    !isCompletedChallenge &&
    !isSectionLocked &&
    (isStepAvailableForComplete(props.currentStep) ||
      isStepOverdue(props.currentStep));

  const isUndoButtonShown = !isCompletedChallenge && !isStepUnavailable;

  const stepResolved = isStepResolved(props.currentStep);

  const { isVideo } = useVideoStepData(props.currentStep?.source);
  const { videoStepAccepted, isVideoLoadingError } = useSidebarLayoutBase();
  const [isVideoNotificationVisible, setIsVideoNotificationVisible] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    setIsVideoNotificationVisible(false);
  }, [stepId, videoStepAccepted]);

  const shouldHideButton =
    isMobile && (!hasRicosLoaded || !stepDescriptionLoaded(stepId));

  const CompleteButton =
    isCompleteButtonShown && !shouldHideButton ? (
      <PreviewActionBlockerContainer
        appearance={ActionBlockerAppearance.PopoverAppearance}
        content={t('action-blocker.preview-mode.complete-step')}
        isActionAllowed={false}
      >
        <SidebarLayoutButton
          className={classes.stepViewMainCTA}
          dataHook={props.completeBtnDataHook || null}
          disabled={isResolveStepRequestInProgress || isVideoLoadingError}
          fullWidth={isMobile}
          size={isMobile ? ButtonSIZE.small : ButtonSIZE.medium}
          onClick={async () => {
            setNextFocusPoint(FocusPoint.Bottom);
            if (isVideo && !videoStepAccepted) {
              setIsVideoNotificationVisible(true);
              return;
            }

            if (!isResolveStepRequestInProgress) {
              await props.onStepResolve(props.currentStep);
            }
          }}
        >
          {settings.get(challengeSettings.completeButtonTxt) as string}
        </SidebarLayoutButton>
      </PreviewActionBlockerContainer>
    ) : null;

  const UndoButton =
    isUndoButtonShown && !shouldHideButton ? (
      <PreviewActionBlockerContainer
        appearance={ActionBlockerAppearance.PopoverAppearance}
        content={t('action-blocker.preview-mode.complete-step')}
        isActionAllowed={false}
      >
        <StepUndo className={classes.undo}>
          {settings.get(challengeSettings.undoButtonTxt) as string}
        </StepUndo>
      </PreviewActionBlockerContainer>
    ) : null;

  return (
    <div
      className={st(classes.root, {
        mobile: isMobile,
        align: !cssPBEnabled
          ? settings.get(challengeSettings.sidebarLayoutTextAlignment)
          : null,
        withCSSAlignment: cssPBEnabled,
        rtl: isRTL,
        buttonState: props.buttonState,
        stepResolved,
      })}
      role="region"
      aria-live="polite"
    >
      <>
        {stepResolved ? (
          <p className={classes.textMessage}>
            <TextWithDirection>
              {t('challenge.page.step.completed')}
            </TextWithDirection>
          </p>
        ) : null}

        {stepResolved ? UndoButton : CompleteButton}

        {isVideoNotificationVisible ? (
          <p className={classes.textMessage}>
            <TextWithDirection>
              {t('challenge.page.step-video-required.warning')}
            </TextWithDirection>
          </p>
        ) : null}

        <StepsNavigation
          stepId={props.currentStep?.id}
          onChangeEntity={props.onNextEntity}
        />
      </>
    </div>
  );
};
